// @ts-nocheck
import { useMemo } from "react";
import { Copy, Loader2, XCircle } from "lucide-react";
import type { CarouselGenerationRun, CarouselItem, CarouselLlmEvent } from "@/lib/api";
import { copyText } from "@/lib/carouselGenerationPack";
import { CTA_IO } from "@/lib/carouselStudioMeta";
import LlmJobDebug from "@/components/LlmJobDebug";
import LlmReceiveProgress from "@/components/LlmReceiveProgress";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TabsContent } from "@/components/ui/tabs";
import { StudioSection } from "@/components/studioVoice";
import { cn } from "@/lib/utils";
import { OP_SURFACE } from "@/lib/opSurfaces";

const STEP_ORDER = ["layout_plan", "draft_critique", "draft_place_local", "draft_place", "draft_caption", "review"] as const;

const STEP_LABEL: Record<string, string> = {
  layout_plan: "Układ",
  draft_critique: "Krytyka pomysłu",
  draft_place_local: "Funkcja lokalna: wstawianie",
  draft_place: "Wstawianie (LLM)",
  draft_caption: "Opis",
  review: "Recenzja",
};

function pretty(value: unknown): string {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    const t = value.trim();
    if ((t.startsWith("{") || t.startsWith("[")) && t.length > 1) {
      try {
        return JSON.stringify(JSON.parse(t), null, 2);
      } catch {
        return value;
      }
    }
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function eventLabel(event?: string): string {
  if (event === "alive") return "czekam na znak";
  if (event === "prompt_ready") return "gotowe zapytanie";
  if (event === "call") return "wysyłam";
  if (event === "first_token") return "leci tekst";
  if (event === "response") return "koniec strzału";
  if (event === "error") return "błąd";
  return event || "?";
}

function runResult(run: CarouselGenerationRun, item?: CarouselItem | null): unknown {
  if (run.copy && Object.keys(run.copy).length) return run.copy;
  if (run.slides?.length) return run.slides;
  if (run.discussion && Object.keys(run.discussion).length) return run.discussion;
  if (run.step === "layout_plan") return item?.layout_plan || run.raw;
  if (run.step === "draft_critique") return item?.discussion?.draft_critique || run.parsed || run.raw;
  if (run.step === "draft_caption") return { caption: item?.caption || run.caption, engage: item?.caption_engage };
  if (run.step === "review") return item?.discussion?.copy_review || run.discussion || run.raw;
  if (run.step === "draft_place" || run.step === "draft_place_local") {
    return item?.copy || run.copy || run.raw;
  }
  return run.raw;
}

function DraftStepCard({
  run,
  item,
  defaultOpen,
}: {
  run: CarouselGenerationRun;
  item?: CarouselItem | null;
  defaultOpen: boolean;
}) {
  const label = STEP_LABEL[run.step] || run.step;
  const kind = run.source === "local" ? "funkcja lokalna" : "strzał modelu";
  const prompt = String(run.prompt || "");
  const raw = pretty(run.raw);
  const result = pretty(runResult(run, item));
  const usage = run.usage || {};
  return (
    <details open={defaultOpen} className="rounded-xl border border-slate-200 bg-white">
      <summary className="cursor-pointer list-none px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              {label}
              <span className="ml-2 text-xs font-normal text-slate-500">{kind}</span>
            </p>
            <p className="truncate text-xs text-slate-500">
              {run.model || (run.source === "local" ? "Python" : "brak modelu")}
              {usage.in || usage.out
                ? ` · in ${usage.in || 0} / out ${usage.out || 0}${usage.think ? ` / think ${usage.think}` : ""}`
                : ""}
              {run.created_at ? ` · ${run.created_at.slice(11, 19)}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {run.error ? (
              <span className="inline-flex items-center gap-1 text-xs text-destructive">
                <XCircle className="h-3.5 w-3.5" /> błąd
              </span>
            ) : (
              <span className="text-xs text-emerald-700">zapisane</span>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void copyText(
                  [`# ${label}`, `source: ${run.source || "?"}`, "", "## Prompt", prompt, "", "## Raw", raw, "", "## Wynik", result].join(
                    "\n",
                  ),
                );
              }}
            >
              <Copy className="mr-1 h-3.5 w-3.5" />
              Kopiuj
            </Button>
          </div>
        </div>
        {run.error ? <p className="mt-2 text-xs text-destructive">{run.error}</p> : null}
      </summary>
      <div className="border-t border-slate-100 px-4 py-3">
        <div className="grid gap-3 xl:grid-cols-3">
          <TraceBlock title="Prompt" body={prompt || "brak"} />
          <TraceBlock title="Surowa odpowiedź" body={raw || "brak"} />
          <TraceBlock title="Wynik" body={result || "brak"} />
        </div>
      </div>
    </details>
  );
}

function TraceBlock({ title, body }: { title: string; body: string }) {
  return (
    <div className="min-w-0 rounded-lg border border-slate-200 bg-slate-50/80">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
        <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={() => void copyText(body)}>
          <Copy className="h-3.5 w-3.5" />
        </Button>
      </div>
      <pre className="max-h-[28rem] overflow-auto whitespace-pre-wrap break-words p-3 font-mono text-[11px] leading-relaxed text-slate-800">
        {body}
      </pre>
    </div>
  );
}

export default function DraftTraceTab({ ctx }: { ctx: any }) {
  const {
    item,
    draftText,
    catalogLabel,
    catalog,
    catalogTypes,
    pain,
    formatId,
    styleId,
    fromDraftRunning,
    layoutPlanRunning,
    fromDraftM,
    layoutPlanM,
    clickAi,
    abortLlmM,
    aiAbort,
    copyBusy,
    composeMode,
    goTab,
  } = ctx;
  const progress = item?.llm_progress;
  const running = Boolean(fromDraftRunning || layoutPlanRunning);
  const failed = Boolean(fromDraftM?.isError || layoutPlanM?.isError || item?.llm_status === "error");
  const statusLabel = fromDraftM?.isError
    ? (fromDraftM.error as Error)?.message || "Błąd wstawiania szkicu."
    : layoutPlanM?.isError
      ? (layoutPlanM.error as Error)?.message || "Błąd planu układu."
      : item?.llm_status === "error"
        ? item.llm_detail || "Błąd modelu."
        : item?.llm_detail || (running ? "W toku…" : item?.layout_plan || item?.copy?.slides?.length ? "Gotowe." : "Czeka na start.");

  const runs = useMemo(() => {
    const rows = [...(item?.generation_runs || [])] as CarouselGenerationRun[];
    rows.sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
    return rows.filter((r) => STEP_ORDER.includes(r.step as (typeof STEP_ORDER)[number]) || String(r.step || "").startsWith("draft_"));
  }, [item?.generation_runs]);

  const livePrompt = String(progress?.prompt || "").trim();
  const activeStep = progress?.step || runs[runs.length - 1]?.step || "";

  const usageEvents = (((item?.usage as { events?: Array<Record<string, unknown>> } | undefined)?.events) ||
    []) as Array<Record<string, unknown>>;
  const llmEvents = (progress?.events || []) as CarouselLlmEvent[];

  if (composeMode === "ai") {
    return (
      <TabsContent value="draft" className="mt-0 space-y-4 focus-visible:outline-none">
        <StudioSection
          role="info"
          title="Szkic (tor AI)"
          hint="Ta zakładka jest pod tor własnego tekstu. W torze AI idź w Przetwarzanie i złóż 4 koncepcje."
        >
          <Button type="button" variant="outline" onClick={() => goTab("copy")}>
            Przejdź do Przetwarzania
          </Button>
        </StudioSection>
      </TabsContent>
    );
  }

  return (
    <TabsContent value="draft" className="mt-0 space-y-4 focus-visible:outline-none">
      <StudioSection
        role="next"
        id="studio-now"
        title="Szkic na slajdy"
        hint={`${CTA_IO.fromDraft.take} ${CTA_IO.fromDraft.give}`}
        className="sticky top-0 z-20 -mx-1 backdrop-blur"
        actions={
          <div className="flex flex-wrap gap-2">
            {aiAbort && running ? (
              <Button type="button" variant="outline" disabled={abortLlmM?.isPending} onClick={() => aiAbort()}>
                Przerwij
              </Button>
            ) : null}
            <Button
              type="button"
              variant="brand"
              disabled={copyBusy || !String(draftText || "").trim() || running}
              onClick={() => clickAi("fromDraft", () => fromDraftM.mutate())}
            >
              {running ? "W toku…" : "Uruchom ponownie"}
            </Button>
          </div>
        }
      >
        <p className={cn("text-sm font-medium", failed ? "text-destructive" : "text-slate-800")}>
          {running ? <Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> : null}
          {statusLabel}
        </p>
        <div>
          <p className="mb-1 text-xs font-medium text-slate-500">Przetwarzanie</p>
          <LlmReceiveProgress progress={progress} llmStatus={item?.llm_status} fallbackStartedAt={progress?.started_at} />
        </div>
      </StudioSection>

      <div className="grid gap-4 xl:grid-cols-[minmax(18rem,26%)_minmax(0,1fr)]">
        <aside className="space-y-4">
          <Card className={cn("border-slate-200", OP_SURFACE.params.box)}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Wejście</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <MetaRow label="Ból" value={pain ? catalogLabel(catalog?.pains, pain) : "wszystkie"} />
              <MetaRow label="Typ" value={formatId ? catalogLabel(catalogTypes, formatId) : "mieszanka"} />
              <MetaRow label="Styl" value={catalogLabel(catalog?.styles, styleId, styleId)} />
              <MetaRow label="Szkic" value={String(draftText || "").trim() ? `${String(draftText).trim().length} znaków` : "brak"} />
              <div className={cn("max-h-64 overflow-auto p-3 whitespace-pre-wrap text-xs", OP_SURFACE.log.box)}>
                {String(draftText || "").trim() || "Wklej szkic na Wejściu."}
              </div>
            </CardContent>
          </Card>

          <div className="space-y-1 rounded-md border border-dashed border-slate-300 bg-slate-50/60 p-3 text-xs text-slate-600">
            <p className="font-medium text-slate-700">Tokeny (info)</p>
              {usageEvents.length ? (
                usageEvents
                  .slice(-12)
                  .reverse()
                  .map((ev, i) => (
                    <p key={`${ev.step || "u"}-${i}`}>
                      {String(ev.step || "?")} · {String(ev.model || "")} · in {Number(ev.in || 0)} / out {Number(ev.out || 0)}
                    </p>
                  ))
              ) : (
                <p>Jeszcze brak usage.</p>
              )}
          </div>

          <LlmJobDebug item={item} progress={progress} className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-3" />

          <div className="max-h-56 space-y-1 overflow-auto rounded-md border border-dashed border-slate-300 bg-slate-50/60 p-3 text-xs text-slate-600">
            <p className="mb-1 font-medium text-slate-700">Log zdarzeń (info)</p>
              {llmEvents.length ? (
                [...llmEvents].reverse().map((ev, i) => (
                  <p key={`${ev.at || i}-${ev.event}`}>
                    {String(ev.at || "").slice(11, 19)} · {eventLabel(ev.event)}
                    {ev.model ? ` · ${ev.model}` : ""}
                    {ev.detail ? ` · ${ev.detail}` : ""}
                  </p>
                ))
              ) : (
                <p>Log pojawi się po starcie joba.</p>
              )}
          </div>
        </aside>

        <section className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-base font-semibold text-slate-900">Oś kroków</h3>
            <p className="text-xs text-slate-500">Prompt → wywołanie → wynik. Bez udawania tool-calli Gemini.</p>
          </div>

          {livePrompt && running ? (
            <Card className="border-brand/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Live prompt ({progress?.step || "?"})</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] text-slate-800">
                  {livePrompt}
                </pre>
              </CardContent>
            </Card>
          ) : null}

          {!runs.length && !running ? (
            <Card>
              <CardContent className="py-8 text-sm text-slate-600">
                Jeszcze nic. Kliknij „Zredaguj slajdy” na Wejściu albo „Uruchom ponownie” tu.
              </CardContent>
            </Card>
          ) : null}

          {running && !runs.length ? (
            <Card>
              <CardContent className="flex items-center gap-2 py-6 text-sm text-slate-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                Czekam na pierwszy zapis kroku…
              </CardContent>
            </Card>
          ) : null}

          {runs.map((run, idx) => {
            const key = run.id || `${run.step}-${run.created_at}`;
            const isOpen = run.step === activeStep || (!activeStep && idx === runs.length - 1);
            return <DraftStepCard key={key} run={run} item={item} defaultOpen={isOpen} />;
          })}

          {(item?.copy?.slides?.length || item?.layout_plan?.slides?.length) && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Wynik na karuzeli</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p>
                  Plan: {item?.layout_plan?.slides?.length || 0} ścian · Copy: {item?.copy?.slides?.length || 0} slajdów
                </p>
                <Button type="button" variant="brand" onClick={() => goTab("copy")}>
                  Przejdź do Przetwarzania
                </Button>
              </CardContent>
            </Card>
          )}
        </section>
      </div>
    </TabsContent>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-medium text-slate-900">{value}</span>
    </div>
  );
}
